import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { House, Quotes, ChatCircleText, Megaphone, Buildings, EnvelopeSimple, Lock, Briefcase, UserPlus, ShieldCheck, Scroll, Phone, PaperPlaneTilt, WhatsappLogo, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import InfoModal from "./InfoModal";

/**
 * Inline Quick Links section — rendered at the bottom of the landing page.
 * "Info" links (企業理念, 挨拶, ...) open as popups.
 * Navigation links (ホーム, 管理者ログイン) navigate as normal routes.
 */
export default function QuickLinksSection() {
    const [data, setData] = useState(null);
    const [infoSlug, setInfoSlug] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/settings/public");
            setData(data);
        } catch {
            setData(null);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const op = data?.operator || {};

    // popup: opens InfoModal; nav: navigates
    const section1 = [
        { label: "ホーム",           Icon: House,           kind: "nav",   target: "/" },
        { label: "企業理念",         Icon: Quotes,          kind: "popup", slug: "philosophy" },
        { label: "挨拶",             Icon: ChatCircleText,  kind: "popup", slug: "greeting" },
        { label: "お知らせ",         Icon: Megaphone,       kind: "popup", slug: "news" },
        { label: "運営会社について", Icon: Buildings,       kind: "popup", slug: "about" },
        { label: "お問い合わせ",     Icon: EnvelopeSimple,  kind: "popup", slug: "contact" },
        { label: "管理者ログイン",   Icon: Lock,            kind: "nav",   target: "/login" },
    ];
    const section2 = [
        { label: "採用情報",     Icon: Briefcase,    kind: "popup", slug: "careers" },
        { label: "会員募集",     Icon: UserPlus,     kind: "popup", slug: "members" },
        { label: "個人情報取扱", Icon: ShieldCheck,  kind: "popup", slug: "privacy" },
        { label: "特定商取引法", Icon: Scroll,       kind: "popup", slug: "tokushoho" },
    ];

    const handleClick = (l) => {
        if (l.kind === "popup") setInfoSlug(l.slug);
        else navigate(l.target);
    };

    const renderBtn = (l) => (
        <button
            key={l.label}
            onClick={() => handleClick(l)}
            className="flex items-center gap-2 px-3 py-2.5 border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm hover:border-[var(--prc-green)] hover:bg-[rgba(0,255,156,0.04)] transition text-left"
            data-testid={`ql-${l.label}`}
        >
            <l.Icon size={16} className="text-[var(--prc-green)] flex-shrink-0" />
            <span className="text-sm text-[var(--prc-ink-2)]">{l.label}</span>
        </button>
    );

    return (
        <>
            <section className="border-t border-[var(--prc-border)] bg-[var(--prc-surface)]" data-testid="quick-links-section">
                <div className="max-w-7xl mx-auto px-6 py-14">
                    <div className="flex items-center gap-2 mb-8">
                        <span className="w-2 h-2 rounded-full bg-[var(--prc-green)]" style={{ boxShadow: "0 0 8px var(--prc-green)" }} />
                        <p className="prc-label-overline !text-[var(--prc-green)]">// QUICK LINKS</p>
                    </div>
                    <div className="grid gap-10 lg:grid-cols-3">
                        <div>
                            <p className="prc-label-overline mb-3">// ナビゲーション</p>
                            <div className="grid grid-cols-2 gap-2">{section1.map(renderBtn)}</div>
                        </div>
                        <div>
                            <p className="prc-label-overline mb-3">// 情報</p>
                            <div className="grid grid-cols-2 gap-2">{section2.map(renderBtn)}</div>

                            <p className="prc-label-overline mb-3 mt-6">// 営業時間</p>
                            <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-4 text-sm flex items-start gap-2.5">
                                <Clock size={24} className="text-[var(--prc-green)] mt-0.5 flex-shrink-0" weight="fill" />
                                <p className="text-[var(--prc-ink-2)] whitespace-pre-wrap leading-relaxed">
                                    {op.business_hours || "—"}
                                </p>
                            </div>
                        </div>
                        <div>
                            <p className="prc-label-overline mb-3">// 連絡先</p>
                            <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-4 space-y-2.5 text-sm">
                                {op.legal_name && <p className="font-semibold text-[var(--prc-ink-2)]">{op.legal_name}</p>}
                                {op.postal_code && <InfoRow label="〒" value={op.postal_code} />}
                                {op.address && <InfoRow label="住所" value={op.address} />}
                                {op.tel && (
                                    <InfoRow Icon={Phone} label="TEL" value={<a href={`tel:${op.tel}`} className="text-[var(--prc-green)] hover:underline">{op.tel}</a>} />
                                )}
                                {op.fax && <InfoRow label="FAX" value={op.fax} />}
                                {op.support_email && (
                                    <InfoRow Icon={PaperPlaneTilt} label="Mail" value={<a href={`mailto:${op.support_email}`} className="text-[var(--prc-green)] hover:underline break-all">{op.support_email}</a>} />
                                )}
                                {op.whatsapp && (
                                    <InfoRow Icon={WhatsappLogo} label="WhatsApp" value={<a href={`https://wa.me/${op.whatsapp.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer" className="text-[var(--prc-green)] hover:underline">{op.whatsapp}</a>} />
                                )}
                                {op.line_id && <InfoRow label="LINE ID" value={op.line_id} />}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <InfoModal slug={infoSlug} onClose={() => setInfoSlug(null)} />
        </>
    );
}

function InfoRow({ Icon, label, value }) {
    return (
        <div className="flex items-start gap-2">
            {Icon ? <Icon size={16} className="text-[var(--prc-muted)] mt-0.5 flex-shrink-0" /> : <span className="w-3 text-[10px] font-mono text-[var(--prc-muted)] mt-0.5">•</span>}
            <div className="flex-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--prc-muted)] mr-1.5">{label}</span>
                <span className="text-[var(--prc-ink-2)]">{value}</span>
            </div>
        </div>
    );
}
